import React, { useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
    Box,
    Container,
    Typography,
    Card,
    CardContent,
    CircularProgress,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Paper,
    Avatar,
    IconButton,
    Link,
    Grid,
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    YouTube as YouTubeIcon,
    PlayArrow as PlayArrowIcon,
    SwipeLeft as SwipeLeftIcon,
} from '@mui/icons-material';
import apiService from '../services/api';
import { getS3Url, ensureDirectS3Url } from '../utils/imageUtils';
import { ROUTES_NAMES } from '../components/Layout';
import { useBrandExplorerStore } from '../stores/brandExplorerStore';
import { useTrackerStore } from '../stores/trackerStore';
import { useAuth } from '../contexts/AuthContext';

const formatNumber = (num: number): string => new Intl.NumberFormat('es-ES').format(num);
const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
};
const generateYouTubeUrl = (url: string | undefined, startTime: number): string | null => {
    if (!url) return null;
    if (url.includes('youtube.com') || url.includes('youtu.be')) {
        const urlObj = new URL(url);
        urlObj.searchParams.set('t', Math.floor(startTime).toString() + 's');
        return urlObj.toString();
    }
    return null;
};

const TrackerLivestreamProgramDetail: React.FC = () => {
    const { programId } = useParams<{ programId: string }>();
    const navigate = useNavigate();
    const { selectedBrand } = useBrandExplorerStore();
    const { startDate: trackerStartDate, endDate: trackerEndDate } = useTrackerStore();
    const { getCompanyTrackerBrandId } = useAuth();
    const effectiveBrandId = getCompanyTrackerBrandId() || selectedBrand?._id;

    const [orderBy, setOrderBy] = useState<'publishedAt' | 'title' | 'peakViewers' | 'averageViewers' | 'liveViewers' | 'chatMessageCount' | 'chatUniqueUsers'>('publishedAt');
    const [order, setOrder] = useState<'asc' | 'desc'>('desc');

    const { data, isLoading, error } = useQuery({
        queryKey: ['brand-livestream-program-detail', effectiveBrandId, programId, trackerStartDate, trackerEndDate],
        queryFn: () => apiService.getBrandLivestreamProgramDetail(effectiveBrandId!, programId!, trackerStartDate, trackerEndDate),
        enabled: !!effectiveBrandId && !!programId,
        staleTime: 10 * 60 * 1000,
        gcTime: 30 * 60 * 1000,
    });

    const handleRequestSort = (property: 'publishedAt' | 'title' | 'peakViewers' | 'averageViewers' | 'liveViewers' | 'chatMessageCount' | 'chatUniqueUsers') => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const sortedMentions = useMemo(() => {
        if (!data?.mentions) return [];
        return [...data.mentions].sort((a, b) => {
            let aVal: number | string;
            let bVal: number | string;
            switch (orderBy) {
                case 'publishedAt':
                    aVal = new Date(a.content.publishedAt).getTime();
                    bVal = new Date(b.content.publishedAt).getTime();
                    break;
                case 'title':
                    aVal = a.content.title?.toLowerCase() ?? '';
                    bVal = b.content.title?.toLowerCase() ?? '';
                    break;
                case 'peakViewers':
                    aVal = a.content.livestream?.peakViewers ?? 0;
                    bVal = b.content.livestream?.peakViewers ?? 0;
                    break;
                case 'averageViewers':
                    aVal = a.content.livestream?.averageViewers ?? 0;
                    bVal = b.content.livestream?.averageViewers ?? 0;
                    break;
                case 'liveViewers':
                    aVal = a.content.livestream?.concurrentViewersAtMention ?? 0;
                    bVal = b.content.livestream?.concurrentViewersAtMention ?? 0;
                    break;
                case 'chatMessageCount':
                    aVal = a.content.livestream?.chatMessageCount ?? 0;
                    bVal = b.content.livestream?.chatMessageCount ?? 0;
                    break;
                case 'chatUniqueUsers':
                    aVal = a.content.livestream?.chatUniqueUsers ?? 0;
                    bVal = b.content.livestream?.chatUniqueUsers ?? 0;
                    break;
                default:
                    return 0;
            }
            if (typeof aVal === 'string' && typeof bVal === 'string') {
                if (aVal < bVal) return order === 'asc' ? -1 : 1;
                if (aVal > bVal) return order === 'asc' ? 1 : -1;
                return 0;
            }
            if (aVal < bVal) return order === 'asc' ? -1 : 1;
            if (aVal > bVal) return order === 'asc' ? 1 : -1;
            return 0;
        });
    }, [data?.mentions, orderBy, order]);

    const handleBack = () => navigate(ROUTES_NAMES.TRACKER);

    if (isLoading) {
        return (
            <Container maxWidth="xl" sx={{ mt: 4, mb: 4, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress />
            </Container>
        );
    }

    if (error || !data) {
        return (
            <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
                <Alert severity="error">Error al cargar los detalles del programa livestream.</Alert>
            </Container>
        );
    }

    const { brand, program, podcast, statistics } = data;

    return (
        <Container maxWidth="xl" disableGutters sx={{ mt: 4, mb: 4, px: { xs: 2, sm: 3, md: 4 } }}>
            <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <IconButton onClick={handleBack} sx={{ mr: -1 }}>
                        <ArrowBackIcon />
                    </IconButton>
                    {program.imageS3Key ? (
                        <Avatar src={ensureDirectS3Url(getS3Url(program.imageS3Key))} sx={{ width: 56, height: 56 }} />
                    ) : (
                        <Avatar sx={{ width: 56, height: 56 }}>
                            <PlayArrowIcon />
                        </Avatar>
                    )}
                    <Box>
                        <Typography variant="h1" component="h1" sx={{ fontSize: '2rem', fontWeight: 600, mb: 0.1 }}>
                            {program.name}
                        </Typography>
                        {podcast && (
                            <Typography variant="body1" color="text.secondary">
                                {podcast.title}
                            </Typography>
                        )}
                    </Box>
                </Box>
            </Box>

            {/* Resumen: solo Episodios, Menciones, Espectadores en vivo */}
            {statistics && (
                <Box sx={{ mb: 4 }}>
                    <Typography variant="h6" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
                        Resumen
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h4" color="primary">
                                        {formatNumber(statistics.uniqueEpisodes)}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Episodios
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h4" color="primary">
                                        {formatNumber(statistics.totalMentions)}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Menciones
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h4" color="primary">
                                        {formatNumber(statistics.accumulatedLiveViewers)}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Espectadores en vivo
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h4" color="primary">
                                        {formatNumber(statistics.peakViewers)}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Espectadores peak
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h4" color="primary">
                                        {formatNumber(statistics.averageViewers)}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Espectadores promedio
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                </Box>
            )}

            {/* Tabla de menciones (episodios) */}
            <Box sx={{ mt: 4 }}>
                <Typography variant="h6" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
                    Menciones de Marca (Livestream)
                </Typography>
                {sortedMentions.length > 0 ? (
                    <TableContainer component={Paper} variant="outlined">
                        <Box
                            sx={{
                                display: { xs: 'flex', md: 'none' },
                                alignItems: 'center',
                                gap: 1,
                                px: 2,
                                py: 1,
                                color: 'text.secondary',
                                borderBottom: '1px solid',
                                borderColor: 'divider',
                            }}
                        >
                            <SwipeLeftIcon />
                            <Typography variant="caption">Desliza para ver más</Typography>
                        </Box>
                        <Table size="small" sx={{ tableLayout: 'fixed' }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ width: '28%', padding: { xs: '12px', sm: '16px' } }}>
                                        <TableSortLabel
                                            active={orderBy === 'title'}
                                            direction={orderBy === 'title' ? order : 'asc'}
                                            onClick={() => handleRequestSort('title')}
                                        >
                                            Episodio
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell sx={{ padding: { xs: '12px', sm: '16px' } }}>
                                        <TableSortLabel
                                            active={orderBy === 'publishedAt'}
                                            direction={orderBy === 'publishedAt' ? order : 'desc'}
                                            onClick={() => handleRequestSort('publishedAt')}
                                        >
                                            Fecha
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell align="right" sx={{ padding: { xs: '12px', sm: '16px' } }}>
                                        <TableSortLabel
                                            active={orderBy === 'liveViewers'}
                                            direction={orderBy === 'liveViewers' ? order : 'desc'}
                                            onClick={() => handleRequestSort('liveViewers')}
                                        >
                                            Espectadores en vivo
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell align="right" sx={{ padding: { xs: '12px', sm: '16px' } }}>
                                        <TableSortLabel
                                            active={orderBy === 'peakViewers'}
                                            direction={orderBy === 'peakViewers' ? order : 'desc'}
                                            onClick={() => handleRequestSort('peakViewers')}
                                        >
                                            Peak
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' }, padding: { xs: '12px', sm: '16px' } }}>
                                        <TableSortLabel
                                            active={orderBy === 'averageViewers'}
                                            direction={orderBy === 'averageViewers' ? order : 'desc'}
                                            onClick={() => handleRequestSort('averageViewers')}
                                        >
                                            Promedio
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell align="right" sortDirection={orderBy === 'chatMessageCount' ? order : false} sx={{ display: { xs: 'none', md: 'table-cell' }, padding: { xs: '12px', sm: '16px' } }}>
                                        <TableSortLabel
                                            active={orderBy === 'chatMessageCount'}
                                            direction={orderBy === 'chatMessageCount' ? order : 'desc'}
                                            onClick={() => handleRequestSort('chatMessageCount')}
                                        >
                                            Mensajes en Chat
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell align="right" sortDirection={orderBy === 'chatUniqueUsers' ? order : false} sx={{ display: { xs: 'none', md: 'table-cell' }, padding: { xs: '12px', sm: '16px' } }}>
                                        <TableSortLabel
                                            active={orderBy === 'chatUniqueUsers'}
                                            direction={orderBy === 'chatUniqueUsers' ? order : 'desc'}
                                            onClick={() => handleRequestSort('chatUniqueUsers')}
                                        >
                                            Usuarios en Chat
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell align="right" sx={{ width: 56, padding: { xs: '8px', sm: '16px' } }} />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {sortedMentions.map((mention) => {
                                    const ls = mention.content.livestream;
                                    const youtubeUrl = generateYouTubeUrl(mention.content.url, mention.segmentStart);
                                    return (
                                        <TableRow key={mention._id} hover>
                                            <TableCell sx={{ overflow: 'hidden', padding: { xs: '12px', sm: '16px' } }}>
                                                {youtubeUrl ? (
                                                    <Link
                                                        component="a"
                                                        href={youtubeUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        color="inherit"
                                                        underline="hover"
                                                        sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                                        title={`${mention.content.title} — Ver en YouTube (${Math.floor(mention.segmentStart)}s)`}
                                                    >
                                                        <Typography variant="body2" fontWeight="medium" noWrap component="span">
                                                            {mention.content.title}
                                                        </Typography>
                                                    </Link>
                                                ) : (
                                                    <Typography variant="body2" fontWeight="medium" noWrap title={mention.content.title}>
                                                        {mention.content.title}
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell sx={{ padding: { xs: '12px', sm: '16px' } }}>
                                                <Typography variant="caption">
                                                    {new Date(mention.content.publishedAt).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' })}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="right" sx={{ padding: { xs: '12px', sm: '16px' } }}>
                                                <Typography variant="body2">{ls?.concurrentViewersAtMention != null ? formatNumber(ls.concurrentViewersAtMention) : '—'}</Typography>
                                            </TableCell>
                                            <TableCell align="right" sx={{ padding: { xs: '12px', sm: '16px' } }}>
                                                <Typography variant="body2">{ls?.peakViewers != null ? formatNumber(ls.peakViewers) : '—'}</Typography>
                                            </TableCell>
                                            <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' }, padding: { xs: '12px', sm: '16px' } }}>
                                                <Typography variant="body2">{ls?.averageViewers != null ? formatNumber(ls.averageViewers) : '—'}</Typography>
                                            </TableCell>
                                            <TableCell align="right" sx={{ display: { xs: 'none', md: 'table-cell' }, padding: { xs: '12px', sm: '16px' } }}>
                                                <Typography variant="body2">{ls?.chatMessageCount != null ? formatNumber(ls.chatMessageCount) : '—'}</Typography>
                                            </TableCell>
                                            <TableCell align="right" sx={{ display: { xs: 'none', md: 'table-cell' }, padding: { xs: '12px', sm: '16px' } }}>
                                                <Typography variant="body2">{ls?.chatUniqueUsers != null ? formatNumber(ls.chatUniqueUsers) : '—'}</Typography>
                                            </TableCell>
                                            <TableCell align="right" sx={{ width: 56, padding: { xs: '8px', sm: '16px' } }}>
                                                {youtubeUrl ? (
                                                    <IconButton
                                                        size="small"
                                                        component="a"
                                                        href={youtubeUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        sx={{ color: '#FF0000' }}
                                                        title={`Ver en YouTube (${Math.floor(mention.segmentStart)}s)`}
                                                    >
                                                        <YouTubeIcon fontSize="small" />
                                                    </IconButton>
                                                ) : null}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                ) : (
                    <Alert severity="info">No hay menciones de livestream para este programa en el período seleccionado.</Alert>
                )}
            </Box>
        </Container>
    );
};

export default TrackerLivestreamProgramDetail;
